<template>
    <b-container fluid class="py-2">
        <h4 class="mb-1">Return Abuse Evidence</h4>
        <p class="text-muted">Review and investigate suspicious returns</p>

        <b-row class="mt-3">
            <b-col md="3" v-for="card in statCards" :key="card.key">
                <div
                    class="alert-card shadow-sm p-2 mb-3"
                    :class="card.cardClass"
                >
                    <div class="card-content">
                        <div class="icon-box" :class="card.iconClass">
                            <i :class="card.icon"></i>
                        </div>
                        <div class="card-text">
                            <div class="mb-1 main-title">{{ card.label }}</div>
                            <h5 class="m-0 font-weight-bold">
                                {{ dashboardStats[card.key] }}
                            </h5>
                        </div>
                    </div>
                </div>
            </b-col>
        </b-row>

        <b-card>
            <b-row class="align-items-center">
                <b-col md="8">
                    <div class="search-wrapper d-flex align-items-center mb-2">
                        <i class="fa fa-search search-icon"></i>

                        <input
                            v-model="search"
                            class="search-input"
                            type="text"
                            placeholder="Search by case ID, user, or product..."
                            @input="onSearchChange"
                        />
                    </div>
                </b-col>

                <b-col md="4">
                    <b-form-select
                        v-model="filter"
                        :options="filterOptions"
                        class="filter-select mb-2"
                        @change="onFilterChange"
                    ></b-form-select>
                </b-col>
            </b-row>
        </b-card>

        <b-row>
            <b-col
                v-for="(caseItem, index) in filteredCases"
                :key="index"
                md="6"
                class="mb-4"
            >
                <div class="case-card">
                    <div
                        class="d-flex justify-content-between align-items-start"
                    >
                        <div>
                            <div class="case-id">{{ caseItem.caseId }}</div>
                            <div class="case-product">
                                {{ caseItem.product }}
                            </div>
                        </div>

                        <div class="severity-wrapper">
                            <span
                                class="severity-badge"
                                :class="caseItem.severity.toLowerCase()"
                            >
                                {{ caseItem.severity }}
                            </span>
                        </div>
                    </div>

                    <div class="info-row mt-3">
                        <div class="label">User:</div>
                        <div class="value">{{ caseItem.user }}</div>
                    </div>

                    <div class="info-row">
                        <div class="label">Order ID:</div>
                        <div class="value">{{ caseItem.orderId }}</div>
                    </div>

                    <div class="info-row">
                        <div class="label">Seller:</div>
                        <div class="value">{{ caseItem.seller }}</div>
                    </div>

                    <div class="info-row mb-2">
                        <div class="label">Date:</div>
                        <div class="value">{{ caseItem.date }}</div>
                    </div>

                    <div class="alert alert-danger small mismatch-box p-2">
                        <strong>
                            <i class="fa fa-balance-scale"></i>
                            Weight Mismatch:
                        </strong>
                        {{ caseItem.mismatch.percent }}% lighter <br />

                        <span class="text-muted">
                            Original: {{ caseItem.mismatch.original }}kg |
                            Return: {{ caseItem.mismatch.return }}kg
                        </span>
                    </div>

                    <div
                        class="alert alert-danger small mismatch-box p-2 mt-2"
                        v-if="caseItem.serial"
                    >
                        <strong>
                            <i class="fa fa-barcode"></i>
                            Serial Number Mismatch
                        </strong>
                        <br />
                        <span class="text-muted">
                            Original: {{ caseItem.serial.original }} <br />
                            Return: {{ caseItem.serial.return }}
                        </span>
                    </div>

                    <div class="mt-3">
                        <div class="font-weight-bold mb-1">Photo Evidence</div>

                        <b-row>
                            <b-col cols="6">
                                <div class="photo-box">
                                    <img
                                        v-if="caseItem.evidenceLink"
                                        :src="caseItem.evidenceLink"
                                        alt="Product Image"
                                        class="photo-image"
                                        @error="
                                            $event.target.style.display = 'none'
                                        "
                                    />
                                    <i
                                        v-if="!caseItem.imageLink"
                                        class="fa fa-picture-o photo-icon"
                                    ></i>
                                    <div class="photo-label">Product</div>
                                </div>
                            </b-col>

                            <b-col cols="6">
                                <div class="photo-box">
                                    <img
                                        v-if="caseItem.returnImageLink"
                                        :src="caseItem.returnImageLink"
                                        alt="Return Image"
                                        class="photo-image"
                                        @error="
                                            $event.target.style.display = 'none'
                                        "
                                    />
                                    <i
                                        v-else
                                        class="fa fa-picture-o photo-icon"
                                    ></i>
                                    <div class="photo-label">Return</div>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <div class="mt-3">
                        <b-badge pill variant="warning">{{
                            caseItem.status
                        }}</b-badge>
                    </div>

                    <div class="d-flex mt-3">
                        <b-button
                            block
                            variant="primary"
                            @click="openDetails(caseItem)"
                        >
                            View Details
                        </b-button>

                        <b-button
                            v-if="getCaseActionState(caseItem.caseId) === 'approved'"
                            variant="success"
                            class="ml-2 icon-btn"
                            disabled
                        >
                            <i class="fa fa-check"></i>
                        </b-button>

                        <b-button
                            v-else-if="getCaseActionState(caseItem.caseId) === 'markedAsFraud'"
                            variant="danger"
                            class="ml-2 icon-btn"
                            disabled
                        >
                            <i class="fa fa-times"></i>
                        </b-button>

                        <template v-else>
                            <b-button
                                variant="success"
                                class="ml-2 icon-btn"
                                @click="approveReturnFromCard(caseItem)"
                            >
                                <i class="fa fa-check"></i>
                            </b-button>

                            <b-button
                                variant="danger"
                                class="ml-2 icon-btn"
                                @click="markAsFraudFromCard(caseItem)"
                            >
                                <i class="fa fa-times"></i>
                            </b-button>
                        </template>
                    </div>
                </div>
            </b-col>
        </b-row>

        <b-modal id="case-details-modal" size="lg" scrollable hide-footer>
            <template v-slot:modal-title>
                <strong>Case Details: {{ selectedCase.caseId }}</strong>
            </template>
            <div class="mb-2 text-muted">
                {{ selectedCase.product }}
            </div>
            <b-row>
                <b-col md="6">
                    <h5 class="section-title">Case Information</h5>
                    <div class="info-box">
                        <div class="label">User</div>
                        <div class="value">{{ selectedCase.user }}</div>
                    </div>
                    <div class="info-box">
                        <div class="label">Order ID</div>
                        <div class="value">{{ selectedCase.orderId }}</div>
                    </div>
                    <div class="info-box">
                        <div class="label">Seller</div>
                        <div class="value">{{ selectedCase.seller }}</div>
                    </div>
                    <div class="info-box">
                        <div class="label">Flag Type</div>
                        <div class="value">
                            {{ selectedCase.flag || "Return Abuse" }}
                        </div>
                    </div>
                    <div class="info-box">
                        <div class="label">Date</div>
                        <div class="value">
                            {{ selectedCase.date || "N/A" }}
                        </div>
                    </div>
                    <div class="info-box">
                        <div class="label">Status</div>
                        <div class="value">
                            {{ selectedCase.status || "N/A" }}
                        </div>
                    </div>
                    <h5 class="section-title mt-4">Serial Number Comparison</h5>
                    <div class="info-box">
                        <div class="label">Original Serial</div>
                        <div class="value">
                            {{ selectedCase.serial?.original || "N/A" }}
                        </div>
                    </div>
                    <div class="info-box">
                        <div class="label">Return Serial</div>
                        <div class="value text-success font-weight-bold">
                            {{ selectedCase.serial?.return || "N/A" }}
                        </div>
                    </div>
                </b-col>
                <b-col md="6">
                    <h5 class="section-title">Weight Analysis</h5>
                    <div class="weight-box green-box">
                        <div class="label">Original Weight</div>
                        <div class="value">
                            {{
                                selectedCase.weight?.original ||
                                selectedCase.mismatch?.original ||
                                "N/A"
                            }}
                            kg
                        </div>
                    </div>
                    <div class="weight-box red-box">
                        <div class="label">Return Weight</div>
                        <div class="value">
                            {{
                                selectedCase.weight?.return ||
                                selectedCase.mismatch?.return ||
                                "N/A"
                            }}
                            kg
                        </div>
                    </div>
                    <div class="weight-box yellow-box">
                        <div class="label">Difference</div>
                        <div class="value">
                            {{
                                selectedCase.weight?.diff ||
                                selectedCase.mismatch?.percent ||
                                "N/A"
                            }}% lighter
                        </div>
                    </div>
                </b-col>
            </b-row>
            <h5 class="section-title mt-4">Photo Evidence</h5>
            <b-row>
                <b-col md="6">
                    <div class="photo-box large-photo">
                        <img
                            v-if="selectedCase.evidenceLink"
                            :src="selectedCase.evidenceLink"
                            alt="Product Image"
                            class="photo-image large"
                            @error="$event.target.style.display = 'none'"
                        />
                        <b-icon-image v-else></b-icon-image>
                    </div>
                    <div class="photo-label">Product Image</div>
                </b-col>
                <b-col md="6">
                    <div class="photo-box large-photo">
                        <img
                            v-if="selectedCase.returnImageLink"
                            :src="selectedCase.returnImageLink"
                            alt="Return Image"
                            class="photo-image large"
                            @error="$event.target.style.display = 'none'"
                        />
                        <b-icon-image v-else></b-icon-image>
                    </div>
                    <div class="photo-label">Return Package</div>
                </b-col>
            </b-row>
            <b-row class="my-5 mx-2 justify-content-center">
                <template v-if="caseActionState === 'approved'">
                    <b-col md="5">
                        <b-button
                            variant="primary"
                            class="ap-btn"
                            block
                            disabled
                            >Approved</b-button
                        >
                    </b-col>
                    <b-col md="2">
                        <b-button variant="light" block @click="closeModal"
                            >Close</b-button
                        >
                    </b-col>
                </template>
                <template v-else-if="caseActionState === 'markedAsFraud'">
                    <b-col md="5">
                        <b-button
                            variant="danger"
                            class="frd-btn"
                            block
                            disabled
                            >Marked as fraud</b-button
                        >
                    </b-col>
                    <b-col md="2">
                        <b-button variant="light" block @click="closeModal"
                            >Close</b-button
                        >
                    </b-col>
                </template>
                <template v-else>
                    <b-col md="5">
                        <b-button
                            variant="danger"
                            class="frd-btn"
                            block
                            @click="markAsFraud"
                            >Mark as Fraud</b-button
                        >
                    </b-col>
                    <b-col md="5">
                        <b-button
                            variant="primary"
                            class="ap-btn"
                            block
                            @click="approveReturn"
                            >Approve Return</b-button
                        >
                    </b-col>
                    <b-col md="2">
                        <b-button variant="light" block @click="closeModal"
                            >Close</b-button
                        >
                    </b-col>
                </template>
            </b-row>
        </b-modal>
    </b-container>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
export default {
    name: "ReturnAbuseDashboard",
    data() {
        return {
            dashboardStats: {
                totalCases: 0,
                pendingReview: 0,
                weightMismatch: 0,
                serialMismatch: 0,
            },
            selectedCase: {
                caseId: "",
                product: "",
                user: "",
                orderId: "",
                seller: "",
                flag: "",
                imageLink: "",
                serial: { original: "", return: "" },
                weight: { original: "", return: "", diff: "" },
            },
            search: "",
            filter: "all",
            filterOptions: [
                { value: "all", text: "All" },
                { value: "pending", text: "Pending" },
                { value: "weight", text: "Weight Mismatch" },
                { value: "serial", text: "Serial Mismatch" },
            ],
            caseActionState: null,
            localStorageKeyPrefix: "returnAbuseCase_",
        };
    },
    computed: {
    ...mapState({
        abuseReportData: (state) => state.fraudManagement.abuseReport,
    }),

    abuseReport() {
        return this.abuseReportData?.data?.result || [];
    },

    statCards() {
        return [
            {
                key: "totalCases",
                label: "Total Cases",
                icon: "fa fa-cube",
                cardClass: "total-card",
                iconClass: "total-icon",
            },
            {
                key: "pendingReview",
                label: "Pending Review",
                icon: "fa fa-exclamation-triangle",
                cardClass: "critical-card",
                iconClass: "critical-icon",
            },
            {
                key: "weightMismatch",
                label: "Weight Mismatch",
                icon: "fa fa-balance-scale",
                cardClass: "active-card",
                iconClass: "active-icon",
            },
            {
                key: "serialMismatch",
                label: "Serial Mismatch",
                icon: "fa fa-barcode",
                cardClass: "investigation-card",
                iconClass: "investigation-icon",
            },
        ];
    },

    cases() {
        if (!Array.isArray(this.abuseReport)) return [];

        const getProductName = (items) => {
            const item = Array.isArray(items) ? items[0] : items;
            return item?.name || item?.description || "Unknown Product";
        };

        const getImageLink = (items) => {
            const item = Array.isArray(items) ? items[0] : items;
            return item?.imageLink || null;
        };

        const getSellerName = (item) =>
            item.deliveryowner_name ||
            item.items?.[0]?.providerName ||
            item.items?.providerName ||
            "Unknown Seller";

        const getStatus = (orderStatus, reportStatus) => {
            if (reportStatus === "Awaiting approval") return "Pending";
            if (orderStatus === "Returned") return "Investigating";
            return "Open";
        };

        const getReturnImageLink = (item) =>
            item?.custom?.productComparison?.returnProduct?.link || null;

        const getEvidenceLink = (item) =>
            item?.custom?.evidence?.link || null;

        return this.abuseReport.map((item) => ({
            caseId: `RET-${item._id?.slice(-4) || item.orderid?.slice(-4) || "UNK"}`,
            product: getProductName(item.items),
            user: item.customer_email || item.customer_name || "Unknown",
            orderId: `ORD-${item.orderid || item._id?.slice(-4) || "UNK"}`,
            seller: getSellerName(item),
            imageLink: getImageLink(item.items),
            returnImageLink: getReturnImageLink(item),
            evidenceLink: getEvidenceLink(item),
            date: item.createdat || "N/A",
            severity: "High",
            status: getStatus(item.orderstatus, item.reportstatus),
            flag: "Return Abuse",
            mismatch: {
                percent: 50,
                original: 1.0,
                return: 0.5,
            },
            serial: {
                original:
                    item.items?.serialNumber ||
                    item.items?.[0]?.serialNumber ||
                    "N/A",
                return:
                    item.items?.returnSerialNumber ||
                    item.items?.[0]?.returnSerialNumber ||
                    "N/A",
            },
            weight: {
                original:
                    item.items?.weight ||
                    item.items?.[0]?.weight ||
                    item.mismatch?.original ||
                    "1.0",
                return:
                    item.items?.returnWeight ||
                    item.items?.[0]?.returnWeight ||
                    item.mismatch?.return ||
                    "0.5",
                diff: item.mismatch?.percent || 50,
            },
        }));
    },

    filteredCases() {
        const q = this.search.toLowerCase();

        return this.cases.filter((c) => {
            const matchesSearch =
                c.caseId.toLowerCase().includes(q) ||
                c.user.toLowerCase().includes(q) ||
                c.product.toLowerCase().includes(q) ||
                c.orderId.toLowerCase().includes(q);

            const matchesFilter =
                this.filter === "all" ||
                (this.filter === "pending" && c.status === "Pending") ||
                (this.filter === "weight" && c.mismatch) ||
                (this.filter === "serial" && c.serial);

            return matchesSearch && matchesFilter;
        });
    },
},
    watch: {
        search() {
            this.currentPage = 1;
        },
        filterType() {
            this.currentPage = 1;
        },
    },
    methods: {
        openDetails(caseItem) {
            this.selectedCase = caseItem;
            const key = this.localStorageKeyPrefix + caseItem.caseId;
            this.caseActionState = localStorage.getItem(key);
            this.$bvModal.show("case-details-modal");
        },
        async fetchAbuseReportData() {
            const payload = {
                tablename: "prep_orders",
                fromDate: moment().subtract(1, "year").format("YYYY-MM-DD"),
                toDate: moment().format("YYYY-MM-DD"),
                filters: {
                    orderstatus: "Return Received",
                    ismaster: "true",
                },
            };
            const res = await this.$store.dispatch(
                "fraudManagement/fetchAbuseReport",
                payload,
            );
            this.dashboardStats.totalCases =
                res?.data?.parameters?.pagination?.totalItems || 0;
        },
        onSearchChange() {
            this.currentPage = 1;
        },
        onFilterChange() {
            this.currentPage = 1;
        },
        closeModal() {
            this.$bvModal.hide("case-details-modal");
        },
        markAsFraud() {
            const key = this.localStorageKeyPrefix + this.selectedCase.caseId;
            localStorage.setItem(key, "markedAsFraud");
            this.caseActionState = "markedAsFraud";
        },
        approveReturn() {
            const key = this.localStorageKeyPrefix + this.selectedCase.caseId;
            localStorage.setItem(key, "approved");
            this.caseActionState = "approved";
        },
        getCaseActionState(caseId) {
            const key = this.localStorageKeyPrefix + caseId;
            return localStorage.getItem(key);
        },
        approveReturnFromCard(caseItem) {
            const key = this.localStorageKeyPrefix + caseItem.caseId;
            localStorage.setItem(key, "approved");
            this.$forceUpdate(); 
        },
        markAsFraudFromCard(caseItem) {
            const key = this.localStorageKeyPrefix + caseItem.caseId;
            localStorage.setItem(key, "markedAsFraud");
            this.$forceUpdate(); 
        },
    },
    mounted() {
        this.fetchAbuseReportData();
    },
};
</script>

<style scoped>
.alert-card {
    height: 80px;
    border-radius: 10px;
    background: #fff;
    display: flex;
    align-items: center;
}

.card-content {
    display: flex;
    align-items: flex-start;
}

.card-text {
    margin-left: 0.75rem;
}
.main-title {
    font-size: clamp(13px, 2.6vw, 15px);
    font-weight: 500;
    color: #222;
}

.icon-box {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
}
.total-icon {
    background: #d9eaff;
    color: #4a8ef0;
}
.critical-icon {
    background: #ffdad9;
    color: #e63939;
}
.active-icon {
    background: #ffe7c3;
    color: #ff9e1f;
}
.investigation-icon {
    background: #fff3b6;
    color: #d4a733;
}

.bg-blue-light {
    background: #e8f1ff;
}
.bg-red-light {
    background: #ffecec;
}
.bg-orange-light {
    background: #fff3e5;
}
.bg-purple-light {
    background: #f4eaff;
}

.text-blue {
    color: #3a7afe;
}
.text-red {
    color: #d9534f;
}
.text-orange {
    color: #e67e22;
}
.text-purple {
    color: #9b59b6;
}

.border-blue {
    border-color: #d8e6ff !important;
}
.border-red {
    border-color: #ffd7d7 !important;
}
.border-orange {
    border-color: #ffe5c7 !important;
}
.border-purple {
    border-color: #e6d4ff !important;
}

.metric-title {
    font-size: 15px;
    font-weight: 500;
    color: #333;
}

.metric-value {
    font-size: 20px;
    font-weight: 600;
    margin-top: 4px;
}

.bg-purple {
    background: #a66bff;
}

.text-purple {
    color: #a66bff;
}

.search-wrapper {
    background: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 12px;
    padding: 8px 14px;
    width: 100%;
    height: 46px;
}

.search-icon {
    font-size: 16px;
    color: #999;
    margin-right: 10px;
}

.search-input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 14px;
}
.search-input:focus {
  outline: none;
  box-shadow: none;
}

.filter-select {
    border-radius: 12px !important;
    height: 46px;
    border: 1px solid #dcdfe6;
    padding-left: 12px;
    background-color: #fff !important;
}
.case-card {
    border: 1px solid #e5e5e5;
    border-radius: 14px;
    background: #fff;
    padding: 18px;
}

.case-id {
    font-size: 18px;
    font-weight: 600;
}

.case-product {
    color: #6c757d;
    font-size: 14px;
}

.info-row {
    display: flex;
    margin-bottom: 4px;
}
.label {
    width: 70px;
    color: #6c757d;
    font-size: 14px;
}
.value {
    font-size: 14px;
    font-weight: 500;
}

.mismatch-box {
    border-radius: 8px;
    background: #ffecec;
    border: 1px solid #ffdcdc;
}

.photo-box {
    background: #f5f5f5;
    border-radius: 10px;
    border: 1px solid #ddd;
    height: 180px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
}

.photo-box.large-photo {
    height: 300px;
}

.photo-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
}

.photo-image.large {
    height: 100%;
}

.photo-icon {
    font-size: 30px;
    color: #999;
    margin-bottom: 8px;
}

.photo-label {
    font-size: 13px;
    color: #777;
    margin-top: 8px;
    position: relative;
    z-index: 1;
}

.photo-box img + .photo-label {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
    margin-top: 0;
}

.icon-btn {
    width: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.severity-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
}

.severity-badge {
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
}

.severity-badge.high {
    background: #ff5579;
}
.severity-badge.medium {
    background: #ffb74d;
}
.severity-badge.low {
    background: #4caf50;
}
.modal-dialog-scrollable .modal-body {
    padding: 30px !important;
}
.container-fluid {
    padding: 3px !important;
}

/* Modal Styles */
.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e5e5e5;
}

.info-box {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
}

.info-box:last-child {
    border-bottom: none;
}

.info-box .label {
    font-weight: 600;
    color: #6c757d;
    font-size: 14px;
    min-width: 140px;
}

.info-box .value {
    flex: 1;
    text-align: right;
    color: #333;
    font-size: 14px;
    word-break: break-word;
}

.weight-box {
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 12px;
    border: 1px solid #e5e5e5;
}

.weight-box .label {
    font-size: 13px;
    color: #6c757d;
    margin-bottom: 8px;
    font-weight: 500;
}

.weight-box .value {
    font-size: 20px;
    font-weight: 600;
    color: #333;
}

.weight-box.green-box {
    background: #e8f5e9;
    border-color: #c8e6c9;
}

.weight-box.green-box .value {
    color: #2e7d32;
}

.weight-box.red-box {
    background: #ffebee;
    border-color: #ffcdd2;
}

.weight-box.red-box .value {
    color: #c62828;
}

.weight-box.yellow-box {
    background: #fff9c4;
    border-color: #fff59d;
}

.weight-box.yellow-box .value {
    color: #f57f17;
}
@media (max-width: 767.98px) {
    .alert-card {
        margin-top: 1rem;
        padding: 1rem !important;
    }

    .case-card {
        padding: 1rem;
    }

    .d-flex.mt-3 {
        flex-wrap: wrap;
    }

    .d-flex.mt-3 .btn {
        flex: 1 1 auto;
        margin-bottom: 0.5rem;
    }

    .icon-btn {
        width: 100%;
        margin-left: 0 !important;
        margin-top: 0.5rem;
    }
}

@media (max-width: 565px) {
    .frd-btn {
        margin-bottom: 10px;
    }
    .ap-btn {
        margin-bottom: 10px;
    }
}
</style>
